import './index.css'
import type { CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import GameIconsCrystalBars from '~icons/game-icons/crystal-bars'
import Clock from '~icons/icons-pix/clock'
import type { ITicket } from 'src/features/Coupon/Regular/Ticket/schema'
import dayjs from 'src/utils/dayjs'
import { formatNum } from 'src/utils/formatNum'
import { formatRelativeTime } from 'src/utils/formatTime'

type TicketProps = ITicket & {
  isApplied?: boolean
}

export function Ticket(props: TicketProps) {
  const {
    template,
    // code,
    boostRatePercentage,
    status,
    availableSince,
    availableUntil,
    isApplied = false,
  } = props

  const { t } = useTranslation(['membership'])

  const statusConfig = {
    available: {
      bg: 'bg-[#FEDA1F]',
      textColor: 'text-neutral-800',
      showClock: true,
      label: null, // Show countdown
      showOverlay: false,
    },
    locked: {
      bg: 'bg-[#FEDA1F]',
      textColor: 'text-neutral-800',
      showClock: true,
      label: null, // Show countdown
      showOverlay: false,
    },
    redeemed: {
      bg: 'bg-[#7E22CE]',
      textColor: 'text-white',
      showClock: false,
      label: t('membership:coupon.ticket.used', 'Used'),
      showOverlay: true,
    },
    expired: {
      bg: 'bg-[#8C8C8C]',
      textColor: 'text-white',
      showClock: false,
      label: t('membership:coupon.ticket.expired', 'Expired'),
      showOverlay: false,
    },
    revoked: {
      bg: 'bg-[#8C8C8C]',
      textColor: 'text-white',
      showClock: false,
      label: t('membership:coupon.ticket.revoked', 'Revoked'),
      showOverlay: false,
    },
  }

  const currentStatus = statusConfig[status]

  return (
    <div className="relative border-solid border-4 border-[#B4B9F5] rounded-md overflow-hidden">
      <GameIconsCrystalBars className="absolute right-[50px] top-1/2 -translate-y-1/2 size-20 text-[#B4B9F5] opacity-30 pointer-events-none" />
      <div className="group/ticket isolate flex justify-between p-1">
        <div className="px-3">
          <div className="my-4">{t(template.name)}</div>
          <div className="text-4xl font-bold roboto-condensed">{`${formatNum(boostRatePercentage, { addPrefix: true })}%`}</div>
        </div>
        <div className="flex flex-col-reverse text-[10px] ">
          <div className="border-solid border border-neutral-800 bg-white">
            <div className="flex justify-center border-b border-b-solid border-neutral-800">
              {t('membership:coupon.ticket.valid-time', 'Valid Period')}
            </div>
            <div className="flex items-center px-1">
              <div className="flex flex-col text-right w-8 text-wrap break-words leading-3">
                <div>{dayjs(availableSince).format('YYYY')}</div>
                <div>{dayjs(availableSince).format('MM/DD')}</div>
              </div>
              <div className="ml-2">-</div>
              <div className="text-right w-8 text-wrap break-words leading-3">
                <div>{dayjs(availableUntil).format('YYYY')}</div>
                <div>{dayjs(availableUntil).format('MM/DD')}</div>
              </div>
            </div>
          </div>
          <div
            className={`${currentStatus.bg} ${currentStatus.textColor} rounded-full flex justify-center items-center mb-1 border border-solid border-neutral-800`}
          >
            {currentStatus.showClock && (
              <Clock
                className="w-3 h-3 mr-0.5 svg-stroke-inherit"
                style={{ '--svg-stroke-color': '#262626' } as CSSProperties}
              />
            )}
            {currentStatus.label ?? formatRelativeTime(availableUntil)}
          </div>
        </div>
      </div>
      {currentStatus.showOverlay && (
        <div className="absolute inset-0 bg-black/50 rounded-md" />
      )}
      {isApplied && (
        <div className="absolute left-0 top-0 bg-red-700 text-white text-[10px] px-2 rounded-tl-[4px] rounded-br-[4px]">
          {t('membership:coupon.ticket.in-use')}
        </div>
      )}
    </div>
  )
}
